import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  TextField,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import WorkIcon from '@mui/icons-material/Work';
import { useAuth } from '../../components/auth/AuthContext';
import AdminTabs from '../../components/admin/AdminTabs';
import { User } from '../../types/user';
import { APIHOST } from '../../common';


export default function UsersPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string>('');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(2);
  const [totalCount, setTotalCount] = useState(0);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isAuthenticated = currentUser && (currentUser.userType === 'internal' || currentUser.userType === 'admin');

  const fetchUsers = async (query: string = searchQuery) => {
    try {
      //setLoading(true);
      const params = new URLSearchParams({
        // 'pagination[page]': (page + 1).toString(),
        // 'pagination[pageSize]': rowsPerPage.toString(),
        'sort': 'createdAt:desc'
      });

      if (query.trim()) {
        params.append('filters[$or][0][username][$containsi]', query);
        params.append('filters[$or][1][email][$containsi]', query);
        params.append('filters[$or][2][firstName][$containsi]', query);
        params.append('filters[$or][3][lastName][$containsi]', query);
      }

      const response = await fetch(`${APIHOST}/api/users?${params}&pagination[page]=${page + 1}&pagination[pageSize]=${rowsPerPage}&populate=role`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: User[] = await response.json();
      setUsers(data);
    //   setTotalCount(data.meta.pagination.total);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    // setPage(0); // Reset to first page when searching
    fetchUsers();
  };

  const getUserTypeIcon = (userType?: string) => {
    switch (userType) {
      case 'admin':
        return <AdminPanelSettingsIcon />;
      case 'internal':
        return <WorkIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const getUserTypeColor = (userType?: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (userType) {
      case 'Hotel':
        return 'warning';
      case 'Internal':
        return 'error';
      case 'User':
        return 'primary';
      default:
        return 'default';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Alert severity="warning">
          Please log in to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', py: 4, mt: 8 }}>
      <Container maxWidth="lg">
        <AdminTabs currentPath="/admin/users" />
        
        <Box sx={{ mt: 3 }}>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder="Search users by name, username, or email..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Users Table */}
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: 'primary.main' }}>
                              {getUserTypeIcon(user.userType)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {user.firstName} {user.lastName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                @{user.username}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={user.role?.name || '?'}
                            color={getUserTypeColor(user.role?.name)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            {user.confirmed && (
                              <Chip label="Confirmed" color="success" size="small" />
                            )}
                            {user.blocked && (
                              <Chip label="Blocked" color="error" size="small" />
                            )}
                            {!user.confirmed && (
                              <Chip label="Unconfirmed" color="warning" size="small" />
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(user.createdAt)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}